/**
 * Pastel accent palette — one shared color language for the playful
 * authenticated screens (dashboard, child profiles, org pages). How the
 * inside pages use it: docs/v2-core/STYLE.md.
 *
 * Every value is a LITERAL Tailwind class string: Tailwind's JIT cannot see
 * interpolated class names, so these are written out in full and dropped
 * straight into `className`.
 *
 * Accessibility contract (non-negotiable):
 *  - `chip` places a -600 icon on a -100 tint. The icon is decorative
 *    (aria-hidden) yet still clears the 3:1 non-text-contrast bar.
 *  - `text` is -700 so eyebrow/label text clears AA 4.5:1 BOTH on white and on
 *    the pastel hero bands. Never use a pastel tint as a body-text color.
 *  - `softBg` (-50), `border` (-100) and `ring` (-200) are light washes; any
 *    body text placed on top stays neutral-800/900 and remains AA-safe.
 */

export type AccentName = "sky" | "rose" | "amber" | "emerald" | "violet" | "indigo";

export interface Accent {
  /** Icon-chip surface + icon color (aria-hidden icon on a soft tint). */
  chip: string;
  /** Very light card/section wash. Body text on top stays neutral-800/900. */
  softBg: string;
  /** Hairline border in the accent hue. */
  border: string;
  /** Emphasis/focus ring in the accent hue. */
  ring: string;
  /** Accent text for eyebrows and labels (AA-safe on white and pastel). */
  text: string;
  /** Gradient stops for avatars/decor — white content sits on top. */
  gradient: string;
  /**
   * Open-section wash (docs/v2-core/STYLE.md): a soft diagonal gradient from
   * a light tint to translucent white, with no border. A complete
   * `bg-gradient-*` class string, so drop it in as is. Body text on top stays
   * neutral-700/800/900 (the tint never goes past -100 at 70%).
   */
  wash: string;
  /** Blurred colour-field blob, as in the landing scenes' backdrops. Decorative only. */
  glow: string;
  /** Page-scene gradient stops for `bg-gradient-to-b`, as on the landing scenes. */
  scene: string;
}

export const ACCENTS: Record<AccentName, Accent> = {
  sky: {
    chip: "bg-sky-100 text-sky-600",
    softBg: "bg-sky-50/60",
    border: "border-sky-100",
    ring: "ring-sky-200",
    text: "text-sky-700",
    gradient: "from-sky-400 to-blue-500",
    wash: "bg-gradient-to-br from-sky-100/70 via-white/70 to-white/40",
    glow: "bg-sky-200/50",
    scene: "from-sky-50 via-white to-rose-50/60",
  },
  rose: {
    chip: "bg-rose-100 text-rose-600",
    softBg: "bg-rose-50/60",
    border: "border-rose-100",
    ring: "ring-rose-200",
    text: "text-rose-700",
    gradient: "from-rose-400 to-pink-500",
    wash: "bg-gradient-to-br from-rose-100/70 via-white/70 to-white/40",
    glow: "bg-rose-200/45",
    scene: "from-rose-50 via-white to-amber-50/60",
  },
  amber: {
    chip: "bg-amber-100 text-amber-600",
    softBg: "bg-amber-50/60",
    border: "border-amber-100",
    ring: "ring-amber-200",
    text: "text-amber-700",
    gradient: "from-amber-400 to-orange-500",
    wash: "bg-gradient-to-br from-amber-100/70 via-white/70 to-white/40",
    glow: "bg-amber-200/45",
    scene: "from-amber-50 via-white to-sky-50/60",
  },
  emerald: {
    chip: "bg-emerald-100 text-emerald-600",
    softBg: "bg-emerald-50/60",
    border: "border-emerald-100",
    ring: "ring-emerald-200",
    text: "text-emerald-700",
    gradient: "from-emerald-400 to-teal-500",
    wash: "bg-gradient-to-br from-emerald-100/70 via-white/70 to-white/40",
    glow: "bg-emerald-200/45",
    scene: "from-emerald-50/70 via-white to-sky-50",
  },
  violet: {
    chip: "bg-violet-100 text-violet-600",
    softBg: "bg-violet-50/60",
    border: "border-violet-100",
    ring: "ring-violet-200",
    text: "text-violet-700",
    gradient: "from-violet-400 to-purple-500",
    wash: "bg-gradient-to-br from-violet-100/70 via-white/70 to-white/40",
    glow: "bg-violet-200/45",
    scene: "from-violet-50 via-white to-sky-50/60",
  },
  indigo: {
    chip: "bg-indigo-100 text-indigo-600",
    softBg: "bg-indigo-50/60",
    border: "border-indigo-100",
    ring: "ring-indigo-200",
    text: "text-indigo-700",
    gradient: "from-indigo-400 to-blue-500",
    wash: "bg-gradient-to-br from-indigo-100/70 via-white/70 to-white/40",
    glow: "bg-indigo-200/45",
    scene: "from-indigo-50 via-sky-50/60 to-white",
  },
};

/**
 * Rotation for auto-colored card grids so adjacent cards differ in hue.
 * (Indigo is held back for the fixed `team` domain below.)
 */
export const CARD_ACCENT_CYCLE: AccentName[] = ["sky", "rose", "amber", "emerald", "violet"];

export const accentForIndex = (i: number): AccentName =>
  CARD_ACCENT_CYCLE[i % CARD_ACCENT_CYCLE.length];

/**
 * Domain → accent, so a given section reads as the same color everywhere in
 * the app (a rose "emergency" chip on the dashboard matches the rose
 * "emergency" section on the child profile).
 */
export const SECTION_ACCENT = {
  basic: "sky",
  school: "violet",
  emergency: "rose",
  allergies: "rose",
  medications: "amber",
  care: "emerald",
  routines: "indigo",
  feeding: "amber",
  medical: "violet",
  pickup: "emerald",
  sharing: "violet",
  intake: "amber",
  team: "indigo",
} as const satisfies Record<string, AccentName>;
